// src/components/common/PostHogProvider.js
// Integracion con PostHog para product analytics (autocapture de clicks,
// heatmaps, session replay, funnels, retention).
//
// Complementa a GA4 (que va mas orientado a trafico web / SEO):
//   - GA4        → adquisicion, canales, campanias, SEO
//   - PostHog    → comportamiento en app, funnel de registro, retencion
//
// Config:
//   REACT_APP_POSTHOG_KEY   — API key (public, empieza con phc_)
//   REACT_APP_POSTHOG_HOST  — Default https://us.i.posthog.com (o https://eu.i.posthog.com)
//
// Si REACT_APP_POSTHOG_KEY falta o es placeholder, no se inicializa (seguro para dev).
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import posthog from 'posthog-js';
import { useAuth } from '../../hooks/useAuth';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

const isValidKey = (k) => !!k && /^phc_[a-zA-Z0-9_-]{20,}$/.test(k);

let initialized = false;

/**
 * Devuelve la instancia de posthog si esta inicializado, o null.
 * Usado por el wrapper `track()` para no explotar si el user no seteo la key.
 */
export const getPostHog = () => (initialized ? posthog : null);

const PostHogProvider = () => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();

  // 1) Init una unica vez
  useEffect(() => {
    if (initialized) return;
    if (!isValidKey(POSTHOG_KEY)) return;

    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      // Autocapture: clicks, form submits, page views. Cero instrumentacion manual
      // para las metricas basicas de UX.
      autocapture: true,
      capture_pageview: false, // lo manejamos manualmente para SPA + params
      capture_pageleave: true,
      // Session replay habilitado por default (revisa privacidad antes de prod
      // si trackeas datos sensibles — para AVlink es OK, es LinkedIn-like).
      session_recording: {
        maskAllInputs: false, // los inputs del signup no son sensibles (nombre, pais)
        maskInputOptions: {
          password: true,     // pero SI enmascarar cualquier <input type=password>
        },
      },
      // Deshabilitar en localhost para no ensuciar metricas
      loaded: (ph) => {
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          ph.opt_out_capturing();
        }
      },
    });

    initialized = true;
  }, []);

  // 2) Trackear cambios de ruta como pageviews
  useEffect(() => {
    if (!initialized) return;
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      path: location.pathname + location.search,
      title: document.title,
    });
  }, [location]);

  // 3) Identificar al user autenticado (para funnels y retencion por cohort)
  useEffect(() => {
    if (!initialized) return;
    if (isAuthenticated && user?.id) {
      posthog.identify(user.id, {
        email: user.email,
        user_type: user.user_type || (user.is_company ? 'company' : 'individual'),
        membership_level: user.membership_level || user.subscription_tier || 'bronze',
        created_at: user.created_at,
      });
    } else if (!isAuthenticated) {
      // Cuando el user hace logout, resetear la identidad para que las metricas
      // no mezclen dos users en la misma sesion.
      posthog.reset();
    }
  }, [isAuthenticated, user?.id]);

  return null;
};

export default PostHogProvider;
